import { Box, Button, Flex, IconButton, Text, Tooltip } from 'components'
import { Cog6ToothIcon, GlobeAltIcon, XMarkIcon } from 'components/icons/hero-icons'
import { isEmpty, uniq } from 'lodash-es'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ITrait, TemplateRoute, TemplateRouting } from 'types'
import {
  findValueAssignment,
  getRoutings,
  getTargetGroupIdsFromRoute,
  matchStringArrays,
  moveValueToSelectedGroup,
  removeChildFromRouting,
  removeValueFromTraitRouting,
  resolveRoutePath,
  updateRouting,
  useFullTraitMap,
} from '../../../utils'
import { useTemplate } from '../TemplateProvider'
import NewRoute from './NewRoute'

interface Props {
  initialSelectedValueIds?: string[]
  onEditTrait: (traitKey: number) => void
  onUpdate: (newRoute: TemplateRoute) => void
  route?: TemplateRoute
}

export default function TemplateRouteEditor({
  initialSelectedValueIds,
  onEditTrait,
  onUpdate,
  route,
}: Props) {
  const { template, updateTemplate } = useTemplate()
  const traitMap = useFullTraitMap()
  const routings = getRoutings(template?.routing, route ?? [])
  const routeKey = useMemo(() => JSON.stringify(route ?? []), [route])

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const lastRouteKeyForSelectionRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    if (lastRouteKeyForSelectionRef.current === null) {
      lastRouteKeyForSelectionRef.current = routeKey
      if (initialSelectedValueIds?.length) {
        setSelectedIds([...initialSelectedValueIds])
      }
      return
    }
    if (lastRouteKeyForSelectionRef.current !== routeKey) {
      lastRouteKeyForSelectionRef.current = routeKey
      setSelectedIds([])
    }
  }, [initialSelectedValueIds, routeKey])

  function applyRoutingNodeUpdate(routeIndex: number, updatedNode: TemplateRouting) {
    if (!template?.routing || !route || !traitMap) return

    const parentRoute = route.slice(0, routeIndex)
    const updatedTemplateRouting = updateRouting(
      template.routing,
      parentRoute,
      updatedNode,
      traitMap
    )
    if (!updatedTemplateRouting) return

    updateTemplate({ routing: updatedTemplateRouting })

    if (route.length <= routeIndex) return

    onUpdate(
      resolveRoutePath(updatedTemplateRouting, route.length, route, routings, traitMap)
    )
  }

  function pruneRoute(index: number) {
    if (!template?.routing || !route || !traitMap || index === 0) return

    const removedNode = { key: routings[index].key, type: routings[index].type }
    const parentRouting = { ...routings[index - 1] }
    const parentToChildStep = route[index - 1]
    const updatedParent = removeChildFromRouting(parentRouting, removedNode, parentToChildStep)

    const parentPath = route.slice(0, index - 1)
    const newRouting = updateRouting(template.routing, parentPath, updatedParent, traitMap)
    if (!newRouting) return

    const nextRoute = resolveRoutePath(
      newRouting,
      index - 1,
      route,
      routings,
      traitMap
    )

    updateTemplate({ routing: newRouting })
    onUpdate(nextRoute)
  }

  function getSelectedValues(routeIndex: number, traitKey: number) {
    if (!route) return []

    if (routeIndex === route.length) {
      return selectedIds
    }

    const routing = routings[routeIndex]
    const childRouting = routings[routeIndex + 1]
    return getTargetGroupIdsFromRoute(routing, traitMap![traitKey], route[routeIndex], childRouting)
  }

  function getTargetGroupIds(routeIndex: number, traitKey: number) {
    if (routeIndex === route!.length) return selectedIds

    const routing = routings[routeIndex]
    const childRouting = routings[routeIndex + 1]
    return getTargetGroupIdsFromRoute(routing, traitMap![traitKey], route![routeIndex], childRouting)
  }

  function isActiveRouteGroupLocked(routeIndex: number, activeGroupIds: string[]) {
    if (routeIndex === route!.length) return false
    return activeGroupIds.length === 1
  }

  function isLockedPastRouteValue(
    routeIndex: number,
    valueId: string,
    activeGroupIds: string[]
  ): boolean {
    if (!isActiveRouteGroupLocked(routeIndex, activeGroupIds)) return false
    return activeGroupIds.includes(valueId)
  }

  function selectValue(routeIndex: number, valueId: string) {
    if (!route || !traitMap) return

    const routing = routings[routeIndex]
    if (routing.type === 'layout') return

    const trait = traitMap[routing.key]
    if (!trait) return

    const assignment = findValueAssignment(routing, valueId)
    const isEditingColumn = routeIndex === route.length
    const targetGroupIds = getTargetGroupIds(routeIndex, routing.key)

    if (isLockedPastRouteValue(routeIndex, valueId, targetGroupIds)) return

    if (assignment.type === 'unused') {
      if (isEditingColumn) {
        if (selectedIds.includes(valueId)) {
          const nextSelectedIds = selectedIds.filter(id => id !== valueId)
          setSelectedIds(nextSelectedIds)
          followExistingRoute(routeIndex, nextSelectedIds)
          return
        }

        if (selectedIds.length > 0) {
          applyRoutingNodeUpdate(
            routeIndex,
            moveValueToSelectedGroup(routing, trait, valueId, selectedIds)
          )
          const nextSelectedIds = uniq([...selectedIds, valueId])
          setSelectedIds(nextSelectedIds)
          followExistingRoute(routeIndex, nextSelectedIds)
          return
        }

        const nextSelectedIds = [...selectedIds, valueId]
        setSelectedIds(nextSelectedIds)
        followExistingRoute(routeIndex, nextSelectedIds)
        return
      }

      if (targetGroupIds.length > 0) {
        applyRoutingNodeUpdate(
          routeIndex,
          moveValueToSelectedGroup(routing, trait, valueId, targetGroupIds)
        )
      }
      return
    }

    if (targetGroupIds.includes(valueId)) {
      applyRoutingNodeUpdate(routeIndex, removeValueFromTraitRouting(routing, trait, valueId))
      if (isEditingColumn) {
        const nextSelectedIds = selectedIds.filter(id => id !== valueId)
        setSelectedIds(nextSelectedIds)
        followExistingRoute(routeIndex, nextSelectedIds)
      }
      return
    }

    if (targetGroupIds.length > 0) {
      applyRoutingNodeUpdate(
        routeIndex,
        moveValueToSelectedGroup(routing, trait, valueId, targetGroupIds)
      )
      if (isEditingColumn) {
        const nextSelectedIds = uniq([...selectedIds, valueId])
        setSelectedIds(nextSelectedIds)
        followExistingRoute(routeIndex, nextSelectedIds)
      }
      return
    }

    if (isEditingColumn) {
      const nextSelectedIds = uniq([...selectedIds, valueId])
      setSelectedIds(nextSelectedIds)
      followExistingRoute(routeIndex, nextSelectedIds)
      return
    }

    applyRoutingNodeUpdate(routeIndex, removeValueFromTraitRouting(routing, trait, valueId))
  }

  function selectAllTraitValues(routeIndex: number, valueIds: string[]) {
    if (routeIndex !== route?.length) return
    if (!route) return

    const allSelected = valueIds.every(id => selectedIds.includes(id))
    const nextSelectedIds = allSelected
      ? selectedIds.filter(id => !valueIds.includes(id))
      : uniq([...selectedIds, ...valueIds])

    setSelectedIds(nextSelectedIds)
    followExistingRoute(routeIndex, nextSelectedIds)
  }

  function followExistingRoute(routeIndex: number, fromIds: string[]) {
    if (!traitMap) return
    if ((route?.length ?? 0) === 0 && routeIndex !== 0) return
    if (routeIndex !== route?.length) return
    if (!route) return

    const routing = routings[routeIndex]
    if (routing.type === 'layout') return

    const valueRouteIndex = routing.valueRoutes?.findIndex(valueRoute =>
      matchStringArrays(valueRoute.fromIds, fromIds)
    )
    const matchingValueRouteIndex = valueRouteIndex ?? -1
    if (matchingValueRouteIndex > -1) {
      onUpdate([...route, matchingValueRouteIndex])
      return
    }

    if (!routing.default) return

    const trait = traitMap[routing.key]
    if (!trait) return

    const usedValueIds = routing.valueRoutes?.flatMap(valueRoute => valueRoute.fromIds) || []
    const defaultValueIds = trait.values
      .filter(value => !usedValueIds.includes(value.id))
      .map(v => v.id)

    if (matchStringArrays(defaultValueIds, fromIds)) {
      onUpdate([...route, 'default'])
    }
  }

  if (!template) return null

  if (!route?.length && isEmpty(template?.routing)) {
    return (
      <Flex direction="column" gap={6} maxWidth="480px">
        <Flex direction="column" gap={2}>
          <Text fontWeight="semibold" textStyle="h6">
            Select the first Trait or Layout for your Routing
          </Text>
          <Text color="gray.600" fontSize="sm">
            This will be where all user journeys through Explorer will start.
          </Text>
        </Flex>
        <NewRoute
          onEditTrait={onEditTrait}
          onUpdate={onUpdate}
          route={route}
          routings={[]}
          selectedIds={[]}
          traitMap={traitMap}
        />
      </Flex>
    )
  }

  if (!traitMap || !route) return null

  return (
    <Flex gap={6} flexShrink={0} pb={4} position="relative" w="max-content">
      <ArrowRight />

      {routings.map((routing, index) => {
        if (routing.type === 'layout') {
          const layout = template?.layoutMap?.[routing.key]
          if (!layout) return null

          return (
            <Flex
              key={`layout-${routing.key}`}
              direction="column"
              minWidth="200px"
              bg="#FFFFFF66"
              borderRadius="md"
              alignSelf="flex-start"
              border="1px solid"
              borderColor="gray.200"
              overflow="hidden"
            >
              <Flex justifyContent="space-between" alignItems="center" px={4} py={3}>
                <Text textStyle="h6">Layout</Text>
                {index > 0 && (
                  <IconButton
                    icon={<XMarkIcon boxSize={4} />}
                    variant="ghost"
                    size="sm"
                    onClick={() => pruneRoute(index)}
                    aria-label="Remove layout"
                  />
                )}
              </Flex>
              <Flex px={4} py={3}>
                <Text fontSize="sm" fontWeight="semibold" color="primary.800">
                  {layout.label || 'Layout'}
                </Text>
              </Flex>
            </Flex>
          )
        } else {
          const trait = traitMap[routing.key]
          if (!trait) return null

          const selectedValueIds = getSelectedValues(index, routing.key)
          const traitGroups = getTraitOptionGroups(routing, trait)
          const isEditable = index === route.length
          const allTraitValueIds = trait.values.map(value => value.id)

          return (
            <Flex key={`trait-${routing.key}`} overflow="hidden" flexDirection="column">
              <Flex
                direction="column"
                minWidth="250px"
                bg="#FFFFFF66"
                borderRadius="md"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
              >
                <Flex justifyContent="space-between" alignItems="center" px={4} py={3}>
                  <Flex alignItems="center" gap={1}>
                    {trait.id && (
                      <Tooltip content="Trait shared within workspace">
                        <Box as="span" display="inline-flex">
                          <GlobeAltIcon type="outline" boxSize={4} color="primary.500" />
                        </Box>
                      </Tooltip>
                    )}
                    <Text textStyle="h6">{trait.title.en || 'Draft'}</Text>
                  </Flex>
                  <Flex alignItems="center" gap={0}>
                    <IconButton
                      aria-label="Edit trait"
                      icon={<Cog6ToothIcon boxSize={4} />}
                      onClick={() => onEditTrait(routing.key)}
                      size="sm"
                      variant="ghost"
                    />
                    <IconButton
                      aria-label="Remove trait"
                      icon={<XMarkIcon boxSize={4} />}
                      onClick={() => pruneRoute(index)}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Flex direction="column" p={0} overflow="auto">
                  {trait.isMulti ? (
                    <Box
                      px={4}
                      py={3}
                      onClick={
                        isActiveRouteGroupLocked(index, selectedValueIds)
                          ? undefined
                          : () => selectAllTraitValues(index, allTraitValueIds)
                      }
                      cursor={
                        isActiveRouteGroupLocked(index, selectedValueIds) ? 'default' : 'pointer'
                      }
                      bg={selectedValueIds.length > 0 && isEditable ? '#e8ecff66' : 'transparent'}
                      fontWeight={selectedValueIds.length > 0 ? 'semibold' : 'normal'}
                    >
                      <Text
                        fontSize="sm"
                        color={selectedValueIds.length > 0 ? 'primary.800' : 'gray.700'}
                      >
                        Multi-Select
                      </Text>
                    </Box>
                  ) : (
                    <>
                      {traitGroups.map((group, groupIndex) => {
                        const groupValues = trait.values.filter(value =>
                          group.valueIds.includes(value.id)
                        )

                        return (
                          <Box key={group.id}>
                            {groupIndex > 0 && <Box borderTopWidth={1} borderTopColor="gray.200" />}
                            <Flex px={4} py={2} justifyContent="space-between" alignItems="center">
                              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                                {group.label}
                              </Text>
                              {isEditable && group.valueIds.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => selectAllTraitValues(index, group.valueIds)}
                                >
                                  {group.valueIds.every(id => selectedValueIds.includes(id))
                                    ? 'Deselect all'
                                    : 'Select all'}
                                </Button>
                              )}
                            </Flex>
                            {groupValues.map((value, valueIndex) => {
                              const isLast = valueIndex === groupValues.length - 1
                              const isSelected = selectedValueIds.includes(value.id)
                              const isLockedRouteValue = isLockedPastRouteValue(
                                index,
                                value.id,
                                selectedValueIds
                              )

                              return (
                                <Box
                                  key={value.id}
                                  px={4}
                                  py={3}
                                  onClick={
                                    isLockedRouteValue
                                      ? undefined
                                      : () => selectValue(index, value.id)
                                  }
                                  cursor={isLockedRouteValue ? 'default' : 'pointer'}
                                  bg={isSelected && isEditable ? '#e8ecff66' : 'transparent'}
                                  borderBottomWidth={isLast ? 0 : 1}
                                  borderBottomColor="gray.100"
                                  fontWeight={isSelected ? 'semibold' : 'normal'}
                                >
                                  <Text
                                    fontSize="sm"
                                    color={isSelected ? 'primary.800' : 'gray.700'}
                                  >
                                    {value.title.en}
                                  </Text>
                                </Box>
                              )
                            })}
                          </Box>
                        )
                      })}
                    </>
                  )}
                </Flex>
              </Flex>
            </Flex>
          )
        }
      })}

      <NewRoute
        onEditTrait={onEditTrait}
        onUpdate={onUpdate}
        route={route}
        routings={routings}
        selectedIds={selectedIds}
        traitMap={traitMap}
      />
    </Flex>
  )
}

function ArrowRight() {
  return (
    <Box
      position="absolute"
      top="100px"
      left="50px"
      right="50px"
      transform="translateY(-50%)"
      zIndex={-1}
      pointerEvents="none"
    >
      <svg
        width="100%"
        height="150"
        viewBox="0 0 800 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <path
          d="M0 25 L760 25 L760 15 L790 30 L760 45 L760 35 L0 35 Z"
          fill="#F3F4F6"
          stroke="#E5E7EB88"
          strokeWidth=".5"
        />
      </svg>
    </Box>
  )
}

interface TraitOptionGroup {
  id: string
  label: string
  valueIds: string[]
}

function getTraitOptionGroups(routing: TemplateRouting, trait: ITrait): TraitOptionGroup[] {
  const valueRouteGroups: TraitOptionGroup[] = (routing.valueRoutes ?? []).map(
    (valueRoute, index) => ({
      id: `value-route-${index}`,
      label: 'Group',
      valueIds: valueRoute.fromIds,
    })
  )

  const usedValueIds = new Set(
    (routing.valueRoutes ?? []).flatMap(valueRoute => valueRoute.fromIds)
  )
  const remainingValueIds = trait.values
    .map(value => value.id)
    .filter(valueId => !usedValueIds.has(valueId))

  if (!remainingValueIds.length) return valueRouteGroups

  if (routing.default) {
    return [
      ...valueRouteGroups,
      { id: 'default-group', label: 'Group', valueIds: remainingValueIds },
    ]
  }

  return [
    ...valueRouteGroups,
    { id: 'unused-options', label: 'Unused options', valueIds: remainingValueIds },
  ]
}
